import { api, walletAdjustInput, type WalletAdjustment, type WalletAdjustInput } from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { Alert, Button, Card, Chip, FormGroup, Input, Select } from '../../ui';
import { formatDateTime, formatUsd } from '../../lib/format';

const col = createColumnHelper<WalletAdjustment>();

/**
 * One page serves both /members/wallet/add and /members/wallet/deduct — the
 * form is identical, only the endpoint and the copy change.
 */
export function WalletAdjust() {
  const { mode } = useParams<{ mode: 'add' | 'deduct' }>();
  const action = mode === 'deduct' ? 'deduct' : 'add';

  const form = useForm<WalletAdjustInput>({
    resolver: zodResolver(walletAdjustInput),
    defaultValues: { user_code: '', wallet: 'activation', amount_usd: 0, reason: '' },
  });

  const submit = useMutation({
    mutationFn: (values: WalletAdjustInput) => api.admin.adjustWallet(action, values),
    onSuccess: () => form.reset(),
  });

  // History is shared by both /members/wallet/add and /members/wallet/deduct —
  // it lists every past admin-initiated adjustment regardless of direction, so
  // an admin working either screen can see the full trail.
  const { query, setPage } = useTableQuery({ defaultSort: 'created_at' });
  const { data: history, isLoading: historyLoading, error: historyError } = useQuery({
    queryKey: ['admin', 'wallet-history', query],
    queryFn: () => api.admin.walletHistory(query),
  });

  const columns = useMemo(
    () => [
      col.accessor('created_at', { header: 'Date', cell: (i) => formatDateTime(i.getValue()) }),
      col.accessor('user_code', {
        header: 'Member',
        cell: (i) => `${i.getValue() ?? '—'}${i.row.original.user_name ? ` · ${i.row.original.user_name}` : ''}`,
      }),
      col.accessor('wallet', {
        header: 'Wallet',
        cell: (i) => (i.getValue() === 'activation' ? 'Activation' : i.getValue() === 'income' ? 'Income' : '—'),
      }),
      col.accessor('direction', {
        header: 'Direction',
        cell: (i) => (
          <Chip tone={i.getValue() === 'credit' ? 'success' : 'danger'}>
            {i.getValue() === 'credit' ? 'Add' : 'Deduct'}
          </Chip>
        ),
      }),
      col.accessor('amount_usd', { header: 'Amount (USD)', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('reason', { header: 'Reason', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('admin_name', { header: 'By admin', cell: (i) => i.getValue() ?? '—' }),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title={action === 'add' ? 'Add to Wallet' : 'Deduct from Wallet'}
        description="Manual balance adjustment. Every adjustment is written to the audit log."
        tabs={[
          { label: 'Add', to: '/members/wallet/add' },
          { label: 'Deduct', to: '/members/wallet/deduct' },
        ]}
      />

      <Card>
        {submit.isSuccess ? <Alert tone="success">Wallet adjusted.</Alert> : null}
        {submit.error ? <Alert tone="danger">{(submit.error as Error).message}</Alert> : null}

        <form onSubmit={form.handleSubmit((values) => submit.mutate(values))} noValidate>
          <FormGroup label="Member User ID" error={form.formState.errors.user_code?.message}>
            <Input placeholder="AO123456" {...form.register('user_code')} />
          </FormGroup>

          <FormGroup label="Wallet" error={form.formState.errors.wallet?.message}>
            <Select {...form.register('wallet')}>
              <option value="activation">Activation Wallet</option>
              <option value="income">Income Wallet</option>
            </Select>
          </FormGroup>

          <FormGroup label="Amount (USD)" error={form.formState.errors.amount_usd?.message}>
            <Input type="number" step="0.01" {...form.register('amount_usd')} />
          </FormGroup>

          <FormGroup label="Reason" error={form.formState.errors.reason?.message}>
            <Input placeholder="Why this adjustment is being made" {...form.register('reason')} />
          </FormGroup>

          <Button type="submit" variant={action === 'add' ? 'gold' : 'danger'} disabled={submit.isPending}>
            {submit.isPending ? 'Working…' : action === 'add' ? 'Add Funds' : 'Deduct Funds'}
          </Button>
        </form>
      </Card>

      <DataTable<WalletAdjustment>
        title="Wallet Adjustment History"
        columns={columns}
        data={history?.data}
        meta={history?.meta}
        isLoading={historyLoading}
        error={historyError as Error | null}
        emptyLabel="No admin wallet adjustments yet"
        onPageChange={setPage}
      />
    </>
  );
}
